import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About 841" },
  { id: "strength", label: "Strength" },
  { id: "balance", label: "Balance" },
  { id: "self", label: "Self" },
  { id: "pricing", label: "Pricing" },
];

const LEGAL = [
  { to: "/privacy", label: "Privacy", id: "privacy" },
  { to: "/terms", label: "Terms", id: "terms" },
  { to: "/support", label: "Support", id: "support" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled || open
          ? "bg-white/85 backdrop-blur-xl border-b border-[rgba(26,24,23,0.06)] shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-12">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="group flex items-baseline gap-2"
        >
          <span className="font-serif text-3xl leading-none tracking-tight text-ink">841</span>
          <span className="text-[10px] uppercase tracking-[0.3em] text-gold-dark transition-colors group-hover:text-gold">
            wellness
          </span>
        </button>

        <div className="hidden items-center gap-6 lg:flex xl:gap-7">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-[13px] tracking-wide text-[#5C5450] transition-colors duration-300 hover:text-ink"
            >
              {l.label}
            </button>
          ))}
          <span className="h-4 w-px bg-[rgba(26,24,23,0.12)]" />
          {LEGAL.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className="text-[11px] tracking-wide text-[#8A8079] transition-colors duration-300 hover:text-ink"
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:block">
          <button
            data-testid="nav-join-waitlist-button"
            onClick={() => go("home")}
            className="rounded-full bg-ink px-6 py-2.5 text-[13px] tracking-wide text-sand transition-colors duration-300 hover:bg-gold-dark"
          >
            Join the waitlist
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          aria-label="Menu"
          onClick={() => setOpen(!open)}
          className="text-ink lg:hidden"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="overflow-hidden border-b border-[rgba(26,24,23,0.06)] bg-white/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-4 px-6 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-2xl text-ink"
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-2 flex gap-5 border-t border-[rgba(26,24,23,0.08)] pt-4">
                {LEGAL.map((l) => (
                  <Link
                    key={l.id}
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.id}`}
                    onClick={() => setOpen(false)}
                    className="text-sm text-[#5C5450]"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
              <button
                data-testid="nav-mobile-join-button"
                onClick={() => go("home")}
                className="mt-2 rounded-full bg-ink px-6 py-3 text-sm text-sand"
              >
                Join the waitlist
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
